// Home card layout preferences (which cards are shown / expanded), kept in localStorage.
import { create } from 'zustand';

export type CardId = 'recents' | 'agenda' | 'myWork' | 'assigned' | 'personal' | 'priorities';

export const HOME_CARDS: { id: CardId; title: string; description: string; size: 'sm' | 'lg' }[] = [
  { id: 'recents', title: 'Recents', description: 'Lists, tasks, docs and channels you opened recently', size: 'sm' },
  { id: 'agenda', title: 'Agenda', description: 'Tasks assigned to you that are due today', size: 'sm' },
  { id: 'myWork', title: 'My Work', description: 'Your open, finished and delegated tasks by date', size: 'lg' },
  { id: 'assigned', title: 'Assigned to me', description: 'Tasks assigned to you grouped by status', size: 'lg' },
  { id: 'personal', title: 'Personal List', description: 'A private home for your own to-dos', size: 'sm' },
  { id: 'priorities', title: 'Priorities', description: 'Your urgent and high priority tasks', size: 'sm' },
];

interface HomeCardsState {
  hidden: CardId[];
  expanded: CardId[];
  showGreeting: boolean;
}

const KEY = 'atrium.homeCards';
const DEFAULTS: HomeCardsState = { hidden: [], expanded: [], showGreeting: true };

function load(): HomeCardsState {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<HomeCardsState>;
    return {
      hidden: Array.isArray(raw.hidden) ? raw.hidden : [],
      expanded: Array.isArray(raw.expanded) ? raw.expanded : [],
      showGreeting: raw.showGreeting ?? true,
    };
  } catch {
    return DEFAULTS;
  }
}

export const useHomeCards = create<HomeCardsState>(load);

useHomeCards.subscribe((s) => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ hidden: s.hidden, expanded: s.expanded, showGreeting: s.showGreeting }));
  } catch {
    /* storage unavailable */
  }
});

const toggleIn = (arr: CardId[], id: CardId, on?: boolean) => {
  const has = arr.includes(id);
  const want = on ?? !has;
  return want ? (has ? arr : [...arr, id]) : arr.filter((x) => x !== id);
};

export function setCardVisible(id: CardId, visible: boolean) {
  useHomeCards.setState((s) => ({ hidden: toggleIn(s.hidden, id, !visible) }));
}

export function toggleCardExpanded(id: CardId) {
  useHomeCards.setState((s) => ({ expanded: toggleIn(s.expanded, id) }));
}

export function resetHomeCards() {
  useHomeCards.setState(DEFAULTS);
}
